import React, { useCallback, useEffect, useMemo, useState } from "react";
import { format, startOfMonth } from "date-fns";
import { connect } from "react-redux";
import { fetchStockHistories } from "../../actions/stockHistoryAction";
import ItemAvatar from "../../components/ItemAvatar";
import AkasSelect from "../../components/AkasSelect";
import "./stock-history.css";

const today = format(new Date(), "yyyy-MM-dd");
const monthStart = format(startOfMonth(new Date()), "yyyy-MM-dd");

const movementConfig = {
  in: { label: "Masuk", className: "bg-success-lt text-success", sign: "+" },
  out: { label: "Keluar", className: "bg-danger-lt text-danger", sign: "-" },
  adjustment: { label: "Penyesuaian", className: "bg-warning-lt text-warning", sign: "±" },
};

const sourceConfig = {
  purchase: "Pembelian",
  sale: "Penjualan",
  production: "Produksi",
  stock_opname: "Stock Opname",
  material_process: "Proses Material",
  purchase_adjustment: "Koreksi Pembelian",
  purchase_cancellation: "Pembatalan Pembelian",
  production_adjustment: "Koreksi Produksi",
  production_reversal: "Reversal Produksi",
  sale_cancellation: "Pembatalan Penjualan",
  sale_recipe: "Pemakaian Resep",
  sale_recipe_cancellation: "Pembatalan Pemakaian Resep",
  stock_transfer_out: "Transfer Keluar",
  stock_transfer_in: "Transfer Masuk",
  seller_loss: "Stok Seller Rusak / Hilang",
};

const formatNumber = (value) =>
  Number(value || 0).toLocaleString("id-ID", {
    maximumFractionDigits: 2,
  });

const formatQty = (value, unit) => `${formatNumber(value)} ${unit || ""}`.trim();

const StockHistoryIcon = ({ name, className = "icon icon-1" }) => {
  const icons = {
    search: (
      <>
        <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
        <path d="M21 21l-6 -6" />
      </>
    ),
    timeline: (
      <>
        <path d="M4 6h4l2 4l4 -8l2 4h4" />
        <path d="M4 18h4l2 -4l4 8l2 -4h4" />
      </>
    ),
  };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      {icons[name] || icons.timeline}
    </svg>
  );
};

const SummaryCard = ({ title, value, subtitle, color = "primary" }) => (
  <div className="col-6 col-xl-3">
    <div className={`card card-sm h-100 akas-summary-card stock-ledger-summary tone-${color}`}>
      <div className="card-body">
        <div className="d-flex align-items-center">
          <span className={`avatar bg-${color}-lt text-${color} me-3`}>
            <StockHistoryIcon />
          </span>
          <div className="flex-fill">
            <div className="text-secondary fw-bold text-uppercase small">{title}</div>
            <div className={`h2 mb-0 mt-1 text-${color}`}>{value}</div>
            <div className="text-secondary small mt-1">{subtitle}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const TableSkeletonRows = ({ rows = 7 }) => (
  <>
    {[...Array(rows)].map((_, rowIndex) => (
      <tr key={`stock-history-skeleton-${rowIndex}`} style={{ cursor: "loader" }}>
        <td>
          <div className="placeholder placeholder-lg w-75"></div>
          <div className="placeholder placeholder-sm w-50 mt-2"></div>
        </td>
        <td>
          <div className="d-flex align-items-center">
            <div className="placeholder rounded-circle me-2" style={{ width: 40, height: 40 }}></div>
            <div className="flex-fill">
              <div className="placeholder placeholder-lg w-75"></div>
              <div className="placeholder placeholder-sm w-50 mt-2"></div>
            </div>
          </div>
        </td>
        <td>
          <div className="placeholder placeholder-lg w-50"></div>
        </td>
        <td>
          <div className="placeholder placeholder-lg w-75"></div>
        </td>
        <td>
          <div className="placeholder placeholder-lg w-75"></div>
          <div className="placeholder placeholder-sm w-50 mt-2"></div>
        </td>
        <td className="text-end">
          <div className="placeholder placeholder-lg w-75"></div>
        </td>
        <td className="text-end">
          <div className="placeholder placeholder-lg w-75"></div>
        </td>
        <td className="text-end">
          <div className="placeholder placeholder-lg w-75"></div>
        </td>
        <td>
          <div className="placeholder placeholder-lg w-100"></div>
        </td>
      </tr>
    ))}
  </>
);

const EmptyState = () => (
  <tr>
    <td colSpan="9" className="text-center text-secondary py-5">
      Belum ada perjalanan stock pada filter ini.
    </td>
  </tr>
);

const StockHistoryPage = ({ data, isLoading, error, fetchStockHistories }) => {
  const [dateRange, setDateRange] = useState({ startDate: monthStart, endDate: today });
  const [itemType, setItemType] = useState("material");
  const [movement, setMovement] = useState("all");
  const [search, setSearch] = useState("");

  const fetchData = useCallback(() => {
    fetchStockHistories({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
      item_type: itemType,
      movement_type: movement,
      search,
    });
  }, [dateRange.endDate, dateRange.startDate, fetchStockHistories, itemType, movement, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const rows = useMemo(() => data?.data || [], [data?.data]);
  const summary = data?.summary || {};

  return (
    <div className="page-wrapper stock-ledger-page">
      <div className="page-header d-print-none stock-ledger-header">
        <div className="container-xl">
          <div className="stock-ledger-hero">
            <div className="col">
              <div className="stock-ledger-eyebrow">IMMUTABLE STOCK LEDGER</div>
              <h2 className="page-title">Histori Stok</h2>
              <p>Jejak saldo sebelum dan sesudah setiap mutasi, lengkap dengan sumber serta penanggung jawab.</p>
            </div>
            <div className="col-auto ms-auto d-print-none">
              {isLoading ? (
                <div className="placeholder-glow">
                  <div className="placeholder rounded-3" style={{ width: 170, height: 38 }}></div>
                </div>
              ) : (
                <div className="badge bg-primary-lt fs-3 px-3 py-2">{formatNumber(summary?.total_mutations)} mutasi</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <div className="card mb-3 stock-ledger-filter">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-3">
                  <label className="form-label">Tanggal Awal</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateRange.startDate}
                    onChange={(event) => setDateRange({ ...dateRange, startDate: event.target.value })}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Tanggal Akhir</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateRange.endDate}
                    onChange={(event) => setDateRange({ ...dateRange, endDate: event.target.value })}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Tipe Item</label>
                  <AkasSelect className="form-select" value={itemType} onChange={(event) => setItemType(event.target.value)}>
                    <option value="all">Semua</option>
                    <option value="material">Material</option>
                    <option value="product">Produk</option>
                  </AkasSelect>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Pergerakan</label>
                  <AkasSelect className="form-select" value={movement} onChange={(event) => setMovement(event.target.value)}>
                    <option value="all">Semua</option>
                    <option value="in">Masuk</option>
                    <option value="out">Keluar</option>
                    <option value="adjustment">Penyesuaian</option>
                  </AkasSelect>
                </div>
                <div className="col-md-2">
                  <button className="btn btn-primary w-100" type="button" onClick={fetchData} disabled={isLoading}>
                    {isLoading ? "Memuat..." : "Terapkan"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <SummaryCard title="Total Mutasi" value={formatNumber(summary?.total_mutations)} subtitle="jejak perubahan stok" />
            <SummaryCard title="Stok Masuk" value={formatNumber(summary?.total_in)} subtitle="transaksi penambahan" color="success" />
            <SummaryCard title="Stok Keluar" value={formatNumber(summary?.total_out)} subtitle="transaksi pengurangan" color="danger" />
            <SummaryCard title="Penyesuaian" value={formatNumber(summary?.total_adjustment)} subtitle="opname dan koreksi" color="warning" />
          </div>

          <div className="card stock-ledger-table-card">
            <div className="card-table">
              <div className="card-header">
                <div className="row w-full align-items-center g-2">
                  <div className="col-md-7 col-12">
                    <h3 className="card-title mb-0">Timeline Perjalanan Stok</h3>
                    {isLoading ? (
                      <div className="placeholder-glow mt-2">
                        <div className="placeholder rounded-2" style={{ width: 330, maxWidth: "100%", height: 12 }}></div>
                      </div>
                    ) : (
                      <p className="text-secondary m-0">
                        Urutan mutasi terbaru ditampilkan paling atas agar mudah ditelusuri.
                      </p>
                    )}
                  </div>
                  <div className="col-md-5 col-12 my-md-0 my-2">
                    <div className="input-group input-group-flat w-auto">
                      {isLoading ? (
                        <span className="input-group-text">
                          <div className="spinner-border spinner-border-sm text-primary text-center" role="status"></div>
                        </span>
                      ) : (
                        <span className="input-group-text">
                          <StockHistoryIcon name="search" />
                        </span>
                      )}
                      <input
                        type="text"
                        className="form-control"
                        autoComplete="off"
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Cari item, kategori, catatan..."
                        value={search}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-vcenter table-selectable">
                  <thead className="table-header">
                    <tr>
                      <th>Tanggal</th>
                      <th>Item</th>
                      <th>Tipe</th>
                      <th>Sumber</th>
                      <th>Dibuat Oleh</th>
                      <th className="text-end">Saldo Sebelum</th>
                      <th className="text-end">Perubahan</th>
                      <th className="text-end">Saldo Setelah</th>
                      <th>Catatan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <TableSkeletonRows />
                    ) : rows.length ? (
                      rows.map((item) => {
                        const config = movementConfig[item?.movement_type] || movementConfig.adjustment;

                        return (
                          <tr key={`${item.item_type}-${item.id}`}>
                            <td>
                              <div className="fw-bold">{item?.date}</div>
                              <div className="text-secondary small">{item?.time}</div>
                            </td>
                            <td>
                              <div className="d-flex py-1 align-items-center">
                                <ItemAvatar
                                  name={item?.item_name}
                                  image={item?.image_path || item?.image}
                                  size="md"
                                  className="me-2"
                                />
                                <div className="flex-fill">
                                  <div className="font-weight-medium">{item?.item_name}</div>
                                  <div className="text-secondary small">{item?.category_label || item?.category || "-"}</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-primary-lt text-primary text-capitalize">
                                {item?.item_type === "material" ? "Material" : "Produk"}
                              </span>
                            </td>
                            <td>
                              <div className="fw-bold">{sourceConfig[item?.source_type] || item?.source_type || "-"}</div>
                              <div className="text-secondary small">{item?.reference_no || "-"}</div>
                            </td>
                            <td>
                              <div className="fw-bold">{item?.created_by_name || "-"}</div>
                              <div className="text-secondary small">creator transaksi</div>
                            </td>
                            <td className="text-end text-secondary">
                              {formatQty(item?.balance_before, item?.unit)}
                            </td>
                            <td className="text-end">
                              <span className={`badge ${config.className} fw-bold`}>
                                {config.sign} {formatQty(item?.movement_qty, item?.unit)}
                              </span>
                            </td>
                            <td className="text-end fw-bold">{formatQty(item?.balance_after, item?.unit)}</td>
                            <td className="text-secondary">{item?.note || "-"}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <EmptyState />
                    )}
                  </tbody>
                </table>
              </div>
              <div className="card-footer d-flex align-items-center">
                {isLoading ? (
                  <div className="placeholder-glow w-100">
                    <div className="placeholder rounded-2" style={{ width: 230, height: 12 }}></div>
                  </div>
                ) : (
                  <p className="m-0 text-secondary">
                    Menampilkan <span>{rows.length}</span> perjalanan stock
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  data: state?.stockHistories?.datas,
  isLoading: state?.stockHistories?.loading || false,
  error: state?.stockHistories?.error,
});

export default connect(mapStateToProps, {
  fetchStockHistories,
})(StockHistoryPage);
